// VoiLog 料金プラン定義（Web版 / lib/models/plan_type.dart の移植）
//
// - free: 議事録作成は時間無制限・閲覧は直近 N 件のみ（既定2件・基本議事録のみ）
// - light(¥480): オフライン議事録作成無制限・案件タイムライン全件閲覧
// - standard(¥980): オフライン無制限＋オンラインAI機能（チャット/派生情報）フル開放
//
// Firestore の users/{uid}.plan を canonical 文字列として読む。
// 旧プラン文字列（offline/hybrid/personal/business/field）も後方互換で吸収する
// （Flutter 側 PlanType.fromString と同じマッピング）。

export type PlanType = 'free' | 'light' | 'standard';

// free プランがタイムラインで閲覧できる直近会議数の既定値（DEC-049 / 方針5 [M-6]）。
// 2026-06-18 オーナー判断（DEC-049 更新）で月2時間上限を撤廃し、無料の差別化を
// 閲覧件数へ一本化。既定を 5→2 件に変更。
// 実行時は RemoteConfig キー `free_timeline_view_limit` で動的に上書きできる
// （useFreeTimelineViewLimit / remoteConfig.ts）。本定数はそのフォールバック既定値。
export const FREE_TIMELINE_LIMIT = 2;

// 文字列 → PlanType（後方互換マッピング込み）。
// 未知・null は free に倒す（安全側）。
export function planFromString(value: string | null | undefined): PlanType {
  switch (value) {
    case 'standard':
      return 'standard';
    case 'light':
    case 'offline': // 旧オフラインプラン → light
      return 'light';
    case 'hybrid':
    case 'personal':
    case 'business':
    case 'field': // 旧無制限プラン群 → standard
      return 'standard';
    case 'free':
    default:
      return 'free';
  }
}

// Web ダッシュボード限定の「表示プラン」正規化（2026-06-19 オーナー指示）。
// 無料プランの Web ダッシュボード表示をライトと完全に同一にするための変換。free → light に倒す。
//
// これにより planHasFullTimeline / planCanViewRollup / canViewMeeting が free でも light と同じ
// 判定（議事録一覧は全件表示・まとめセクションも表示）になる。ただし議事録の「中身」は
// 閲覧ゲート planCanBrowseMinutesOnWeb（free のまま据え置き）が webMinutesLocked を立てるため
// モザイク（blur）のまま読めない。つまり free も light も「全件＋まとめ表示・中身はモザイク」で揃う。
//
// 影響範囲は上記3関数の呼び出し側のみ。以下には影響しない（意図的に free のまま据え置く）:
// - planCanBrowseMinutesOnWeb（モザイク化ゲート。free=false 維持＝中身は隠す）
// - planHasAiFeatures（standard 限定。free をライト扱いしても AI は開放しない）
// - planDisplayName（「無料」表示のまま）
// アプリ側（Flutter / plan_type.dart）の制限とは無関係。Web 表示専用の変換。
export function webDisplayPlan(plan: PlanType): PlanType {
  return plan === 'free' ? 'light' : plan;
}

// 日本語表示名。
export function planDisplayName(plan: PlanType): string {
  switch (plan) {
    case 'standard':
      return 'スタンダード';
    case 'light':
      return 'ライト';
    case 'free':
    default:
      return '無料';
  }
}

// オンラインAI機能（チャット・派生情報）がフル利用可能か。standard のみ。
export function planHasAiFeatures(plan: PlanType): boolean {
  return plan === 'standard';
}

// 案件タイムラインを全件閲覧できるか（light 以上）。
// free は直近 FREE_TIMELINE_LIMIT 件のみ。
export function planHasFullTimeline(plan: PlanType): boolean {
  return plan === 'light' || plan === 'standard';
}

// DEC-059: free は議事録の閲覧が「案件内 直近 N 件」のみ。
// 議事録詳細を開いてよいかを、案件内の会議ID（recordedAt 降順）リスト内での
// 対象 meetingId の順位で判定する純関数。light/standard は常に true。
//
// この関数が web 全体の閲覧ゲートの SSOT。タイムラインのロックカード（表示の出し分け）と
// 議事録詳細ページの直アクセスブロック（実際の閲覧禁止）の両方が同じ規則で一致する。
//
// - recentMeetingIdsDesc: 案件内の非削除会議IDを recordedAt 降順に並べたもの。
// - meetingId がリストに無い場合は false（安全側でブロック。直近 N 件に入っていない or 取得漏れ）。
// - freeLimit が 0 以下でも Math.max で 0 に丸め、free は何も開けない方向に倒す。
export function canViewMeeting(
  plan: PlanType,
  recentMeetingIdsDesc: string[],
  meetingId: string,
  freeLimit: number,
): boolean {
  if (planHasFullTimeline(plan)) return true;
  const idx = recentMeetingIdsDesc.indexOf(meetingId);
  if (idx === -1) return false;
  return idx < Math.max(0, freeLimit);
}

// Web ダッシュボードで議事録（一覧・詳細）を閲覧できるか の判定。standard のみ true。
// Web ダッシュボードは「マルチデバイス」特典としてスタンダード（¥980）専用に引き上げる。
// ライト（¥480）は「端末で完結」＝アプリ内のみのコンセプトのため Web 閲覧不可。無料も不可。
// free / light には「議事録はアプリで確認」＋スタンダードへのアップグレード誘導を見せる
// （DEC-059 の Web 直近N件閲覧を supersede。アプリ側の作成・閲覧は全プラン無制限のまま）。
//
// これは「Web で議事録ビュー領域を出すか出さないか」の上位ゲート（一覧・ホーム・案件・相手先タイムライン）。
// 一方 canViewMeeting() は議事録詳細ページの最終防衛線として直近N件フォールバックを引き続き担う
// （上位ゲートを迂回した直リンク・履歴アクセス時の保険）。両者は別レイヤーで併存する。
export function planCanBrowseMinutesOnWeb(plan: PlanType): boolean {
  return plan === 'standard';
}

// 「まとめ（決定 → アクション集約）」を閲覧できるか（S-061-5 / DEC-057 決定内容1）。
// 有料限定＝ライト（¥480）以上。無料プランには まとめ（ProjectRollup）と未完バッジを見せない
// （S-058 既存リーク穴の解消＋スタンダード年払い導線の根幹）。
// ライトとスタンダードの表示差は付けない（けんじ確定 2026-06-13: 有料なら表示・無料なら非表示の単純ゲート）。
export function planCanViewRollup(plan: PlanType): boolean {
  return plan === 'light' || plan === 'standard';
}
